import { createContext, useContext, useState, useMemo } from 'react';
import { createTheme, Theme } from '@mui/material';
import { Shadows } from '@mui/material/styles';
import { deDE } from '@mui/material/locale';
import { deDE as dataGridDe } from '@mui/x-data-grid/locales';

type Mode = 'light' | 'dark';

interface ThemeModeContextValue {
    mode: Mode;
    toggleMode: () => void;
    theme: Theme;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
    mode: 'light',
    toggleMode: () => {},
    theme: createTheme(),
});

const softShadows = Array(25).fill('0px 20px 45px rgba(15, 23, 42, 0.16)') as Shadows;
softShadows[0] = 'none';
softShadows[1] = '0px 2px 8px rgba(15, 23, 42, 0.08)';
softShadows[2] = '0px 6px 18px rgba(15, 23, 42, 0.10)';
softShadows[3] = '0px 12px 30px rgba(15, 23, 42, 0.12)';
softShadows[4] = '0px 18px 40px rgba(15, 23, 42, 0.14)';

export function buildTheme(mode: Mode): Theme {
    const isDark = mode === 'dark';
    return createTheme(
        {
            palette: {
                mode,
                primary: { main: '#123d24', light: '#1f4d2b', dark: '#0f2f1a', contrastText: '#ffffff' },
                secondary: { main: '#c46f2f', light: '#d58a4b', dark: '#9f5622', contrastText: '#ffffff' },
                success: { main: '#2f8f5b' },
                warning: { main: '#d08a3a' },
                error: { main: '#c0392b' },
                background: {
                    default: isDark ? '#121212' : '#f8f5ef',
                    paper: isDark ? '#1e1e1e' : '#ffffff',
                },
                text: {
                    primary: isDark ? '#e8e3d9' : '#1f2328',
                    secondary: isDark ? '#9e9e9e' : '#4c5663',
                },
                divider: isDark ? '#2e2e2e' : '#e6e1d9',
            },
            shadows: softShadows,
            shape: { borderRadius: 10 },
            typography: {
                fontFamily: '"Inter", sans-serif',
                h1: { fontWeight: 700, letterSpacing: '-0.02em', '@media (max-width:600px)': { fontSize: '2.1rem' } },
                h2: { fontWeight: 700, letterSpacing: '-0.02em', '@media (max-width:600px)': { fontSize: '1.8rem' } },
                h3: { fontWeight: 700, letterSpacing: '-0.02em', '@media (max-width:600px)': { fontSize: '1.55rem' } },
                h4: { fontWeight: 700, letterSpacing: '-0.01em' },
                h5: { fontWeight: 600 },
                h6: { fontWeight: 600 },
                button: { fontWeight: 600, textTransform: 'none' },
            },
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: { borderRadius: 8, padding: '6px 18px' },
                        contained: {
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.18)' },
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            boxShadow: isDark
                                ? '0 2px 8px rgba(0,0,0,0.4)'
                                : '0 2px 8px rgba(15,23,42,0.08)',
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: { backgroundImage: 'none' },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundColor: isDark ? '#0f2417' : '#123d24',
                            // Dezenter Verlauf: links dunkler, rechts etwas heller.
                            backgroundImage: isDark
                                ? 'linear-gradient(90deg, #0c2012 0%, #163521 100%)'
                                : 'linear-gradient(90deg, #0f2f1a 0%, #1f4d2b 100%)',
                            boxShadow: '0 2px 12px rgba(15, 47, 26, 0.25)',
                        },
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        paper: {
                            // Warmes Creme statt reinweiß; dezente Trennlinie rechts.
                            backgroundColor: isDark ? '#181818' : '#fbf9f3',
                            borderRight: `1px solid ${isDark ? '#2e2e2e' : 'rgba(20, 35, 25, 0.08)'}`,
                            backgroundImage: 'none',
                        },
                    },
                },
                MuiListItemButton: {
                    styleOverrides: {
                        root: {
                            borderRadius: 10,
                            margin: '2px 8px',
                            '&.Mui-selected': {
                                // Aktive Seite als helle grüne Pill hervorheben.
                                backgroundColor: isDark ? 'rgba(78,143,46,0.22)' : '#e8f1e8',
                                color: isDark ? '#bfe0a8' : '#1f4d2b',
                                '& .MuiListItemIcon-root': {
                                    color: isDark ? '#bfe0a8' : '#1f4d2b',
                                },
                                '&:hover': {
                                    backgroundColor: isDark ? 'rgba(47,107,53,0.30)' : 'var(--pvta-green-100)',
                                },
                            },
                        },
                    },
                },
                MuiDataGrid: {
                    styleOverrides: {
                        root: {
                            border: 'none',
                            backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                            borderRadius: 12,
                            boxShadow: isDark
                                ? '0 2px 8px rgba(0,0,0,0.4)'
                                : '0px 10px 26px rgba(15, 23, 42, 0.08)',
                        },
                        columnHeaders: {
                            backgroundColor: isDark ? '#2a2a2a' : '#f4f1ec',
                            borderBottom: `1px solid ${isDark ? '#2e2e2e' : '#e6e1d9'}`,
                        },
                        columnHeaderTitle: {
                            fontWeight: 700,
                            color: isDark ? '#e8e3d9' : '#30363d',
                        },
                        row: {
                            '&:hover': {
                                backgroundColor: isDark
                                    ? 'rgba(78,143,46,0.12)'
                                    : 'rgba(64,115,37,0.08)',
                            },
                            '&:nth-of-type(even)': {
                                backgroundColor: isDark ? '#242424' : '#fcfbf9',
                            },
                        },
                        cell: {
                            borderBottom: `1px solid ${isDark ? '#2e2e2e' : '#f0ebe4'}`,
                            '&:focus, &:focus-within': { outline: 'none' },
                        },
                        footerContainer: {
                            borderTop: `1px solid ${isDark ? '#2e2e2e' : '#e6e1d9'}`,
                            backgroundColor: isDark ? '#242424' : '#f9f7f3',
                        },
                        overlay: {
                            backgroundColor: isDark ? '#1e1e1e' : '#fbfaf8',
                            color: isDark ? '#9e9e9e' : '#6b727c',
                        },
                        columnSeparator: {
                            color: isDark ? '#2e2e2e' : '#e6e1d9',
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
                            borderRadius: 10,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDark ? '#3a3a3a' : '#e2dcd3',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDark ? '#555' : '#cfc7bb',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#123d24',
                                borderWidth: 2,
                            },
                        },
                    },
                },
            },
        },
        deDE,
        dataGridDe,
    );
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setMode] = useState<Mode>(() => {
        const stored = localStorage.getItem('pvta-theme');
        return (stored === 'dark' || stored === 'light') ? stored : 'light';
    });

    const toggleMode = () => {
        setMode((m) => {
            const next: Mode = m === 'light' ? 'dark' : 'light';
            localStorage.setItem('pvta-theme', next);
            return next;
        });
    };

    const theme = useMemo(() => buildTheme(mode), [mode]);

    return (
        <ThemeModeContext.Provider value={{ mode, toggleMode, theme }}>
            {children}
        </ThemeModeContext.Provider>
    );
}

export const useThemeMode = () => useContext(ThemeModeContext);
